import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "./assets";

const Word = ({ children, i }) => (
  <span className="inline-block overflow-hidden pb-[0.12em] -mb-[0.12em] mr-[0.28em]">
    <motion.span
      className="inline-block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay: 1.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const phrases = ["Trusted Partner in Healthcare", "Protecting Your Workforce", "Enhancing ROI"];

export default function HeroScene() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    setMouse({ x: (e.clientX / window.innerWidth - 0.5) * 2, y: (e.clientY / window.innerHeight - 0.5) * 2 });
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (window.lenis && el) window.lenis.scrollTo(el, { offset: -60 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section onMouseMove={onMove} className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-scene">
      <div className="max-w-[90rem] mx-auto px-5 sm:px-6 md:px-14 w-full grid grid-cols-1 lg:grid-cols-[42%_58%] items-center gap-6 lg:gap-8 pt-44 sm:pt-48 lg:pt-48 pb-16 lg:pb-12">
        <div className="relative z-10 order-1 lg:order-1 text-center lg:text-left">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="overline-label mb-4 relative z-10" data-testid="hero-overline"
          >
            Corporate Healthcare Services • Est. Mumbai
          </motion.p>
          <h1 className="font-display text-[#12294d] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04]" data-testid="hero-heading">
            <Word i={0}>Med<span className="text-[#2ab5cf]">Verify</span></Word>
            <br />
            <Word i={1}>Services</Word>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.8 }}
            className="mt-6 sm:mt-7 max-w-xl mx-auto lg:mx-0 text-left space-y-6 sm:space-y-7"
            data-testid="hero-vision-mission"
          >
            <div className="pl-4 border-l-[3px] border-[#2ab5cf]">
              <p className="text-sm uppercase tracking-[0.14em] font-extrabold text-[#168da6] mb-2">Our Vision</p>
              <p className="text-lg sm:text-xl font-semibold leading-relaxed text-[#12294d]">To create a healthy and happy workforce in every organization.</p>
            </div>
            <div className="pl-4 border-l-[3px] border-[#2ab5cf]">
              <p className="text-sm uppercase tracking-[0.14em] font-extrabold text-[#168da6] mb-2">Our Mission</p>
              <p className="text-lg sm:text-xl font-normal leading-relaxed text-[#12294d]">To empower our corporate partners to build <strong>high-performing teams</strong>, maximize their <strong>ROI</strong>, and foster the greatest <strong>employer–employee relationship</strong>.</p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9, duration: 0.8 }}
            className="mt-5 sm:mt-7 flex flex-wrap justify-center lg:justify-start items-center gap-x-3 gap-y-2 text-sm sm:text-base md:text-lg text-slate-600 font-semibold max-w-lg mx-auto lg:mx-0" data-testid="hero-subtitle"
          >
            {phrases.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-x-3">
                {i > 0 && <span className="text-[#2ab5cf]">•</span>}
                {p}
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.2, duration: 0.8 }}
            className="mt-6 sm:mt-7 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start"
          >
            <button onClick={() => scrollTo("about")} data-testid="hero-cta-about" className="btn-primary rounded-full px-7 sm:px-8 py-3.5 sm:py-4 text-sm font-bold w-full sm:w-auto">
              About MedVerify
            </button>
            <button onClick={() => scrollTo("services")} data-testid="hero-cta-services" className="rounded-full px-7 sm:px-8 py-3.5 sm:py-4 text-sm font-bold inline-flex items-center justify-center gap-2 border border-[#1a3a6b]/25 text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors duration-300 w-full sm:w-auto">
              Explore Our Services <ArrowRight size={16} />
            </button>
            <a href="mailto:info@medverifys.com" data-testid="hero-cta-contact" className="rounded-full px-7 sm:px-8 py-3.5 sm:py-4 text-sm font-bold border border-[#1a3a6b]/25 text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors duration-300 text-center w-full sm:w-auto">
              Get In Touch
            </a>
          </motion.div>
          <motion.button
            type="button"
            onClick={() => scrollTo("about")}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
            className="mt-6 mx-auto lg:hidden flex flex-col items-center gap-1.5 text-[#1a3a6b]/55 hover:text-[#1a3a6b] transition-colors"
            aria-label="Scroll to About MedVerify"
            data-testid="hero-scroll-button-mobile"
          >
            <span className="text-[0.58rem] tracking-[0.3em] uppercase">Scroll</span>
            <span className="scroll-line" />
          </motion.button>
        </div>

        <div className="relative order-2 lg:order-2 mt-2 sm:mt-4 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 1.07 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ x: mouse.x * 10, y: mouse.y * 6 }}
            className="relative"
          >
            <div className="breathe">
              <img
                src={ASSETS.heroConsult}
                alt="Corporate employee in consultation with a healthcare professional"
                className="w-full max-w-[520px] lg:max-w-none mx-auto h-auto scene-blend"
                data-testid="hero-3d-scene"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        type="button"
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#1a3a6b]/55 hover:text-[#1a3a6b] transition-colors z-20"
        aria-label="Scroll to About MedVerify"
        data-testid="hero-scroll-button"
      >
        <span className="text-[0.62rem] tracking-[0.3em] uppercase">Scroll</span>
        <span className="scroll-line" />
      </motion.button>
    </section>
  );
}
